// Mapper for config-version payloads.

import type { AgentConfigVersionView, VersionInfoView } from './types';

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function asString(value: unknown, fallback = ''): string {
  return typeof value === 'string' ? value : fallback;
}

function asNullableString(value: unknown): string | null {
  return typeof value === 'string' ? value : null;
}

function asNumber(value: unknown, fallback = 0): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}

export function toVersionInfo(raw: Record<string, unknown>): VersionInfoView | null {
  const id = raw.id;
  const agentId = raw.agent_id;
  if (typeof id !== 'string' || id.length === 0) return null;
  if (typeof agentId !== 'string' || agentId.length === 0) return null;
  return {
    id,
    agentId,
    versionNumber: asNumber(raw.version_number),
    isActive: raw.is_active === true,
    createdBy: asString(raw.created_by),
    sourceVersionId: asNullableString(raw.source_version_id),
    changeSummary: asNullableString(raw.change_summary),
    createdAt: asString(raw.created_at),
  };
}

export function toAgentConfigVersion(raw: unknown): AgentConfigVersionView | null {
  if (!isRecord(raw)) return null;
  const normalized = raw.id === null && typeof raw.agent_id === 'string'
    ? { ...raw, id: 'manifest' }
    : raw;
  const base = toVersionInfo(normalized);
  if (base === null) return null;
  const payload = isRecord(normalized.payload) ? (normalized.payload as Record<string, unknown>) : {};
  return { ...base, payload };
}

export function toAgentConfigVersionList(raw: unknown): AgentConfigVersionView[] {
  if (!isRecord(raw)) return [];
  const versions = Array.isArray(raw.versions) ? raw.versions : [];
  return versions
    .map(toAgentConfigVersion)
    .filter((v): v is AgentConfigVersionView => v !== null);
}
